import React, { useMemo, useState } from "react";
import img from "../assets/Class.jpg";
import img2 from "../assets/board.avif";
import img3 from "../assets/Jee.avif";
import img4 from "../assets/neet.webp";

type StoryCard = {
  id: number;
  title: string;
  seoTitle: string;
  subtitle: string;
  image: string;
  alt: string;
  tag: string;
  link: string;
  audience: string;
  keywords: string;
};

const stories: StoryCard[] = [
  {
    id: 1,
    title: "Foundation Students Build Strong Concepts",
    seoTitle:
      "Class 7 to 10 Foundation Learning for Maths Science and Reasoning",
    subtitle:
      "Class 7, Class 8, Class 9 and Class 10 learners improve Maths, Science, reasoning and school exam preparation through structured live classes, notes, assignments and mentor support.",
    image: img,
    alt: "Class 7 to 10 foundation students learning Maths Science reasoning and school exam concepts",
    tag: "School Foundation",
    link: "/courses",
    audience:
      "Best for Class 7, Class 8, Class 9 and Class 10 students who want concept clarity, NCERT learning, school exam support and early competitive foundation.",
    keywords:
      "Class 7 online classes, Class 8 online tuition, Class 9 Maths Science coaching, Class 10 foundation course, school foundation program, CBSE NCERT learning, Maths Science reasoning",
  },
  {
    id: 2,
    title: "Board Exam Preparation with Guided Revision",
    seoTitle:
      "CBSE Board Exam Preparation with NCERT Notes Sample Papers and Tests",
    subtitle:
      "Students prepare for board exams with NCERT-based notes, sample papers, chapter-wise tests, revision classes, important questions and regular practice support.",
    image: img2,
    alt: "CBSE board exam preparation with NCERT notes sample papers chapter tests and revision classes",
    tag: "Board Exams",
    link: "/courses",
    audience:
      "Best for Class 10 and Class 12 students preparing for CBSE board exams, NCERT revision, school tests, sample papers and final exam practice.",
    keywords:
      "CBSE board exam preparation, Class 10 board preparation, Class 12 board preparation, NCERT notes, sample papers, board exam revision, chapter wise tests",
  },
  {
    id: 3,
    title: "JEE Preparation with Practice-Focused Learning",
    seoTitle:
      "JEE Main Preparation with Physics Chemistry Maths DPPs and Mock Tests",
    subtitle:
      "JEE aspirants build strong Physics, Chemistry and Mathematics preparation with live classes, DPPs, formula practice, mock tests and performance tracking.",
    image: img3,
    alt: "JEE Main students preparing Physics Chemistry Mathematics with DPPs mock tests and live classes",
    tag: "JEE Main",
    link: "/courses",
    audience:
      "Best for JEE Foundation and JEE Main aspirants who need PCM concept clarity, problem-solving speed, mock test practice and exam strategy.",
    keywords:
      "JEE Main online coaching, JEE Foundation course, IIT JEE preparation, Physics Chemistry Maths coaching, JEE DPP, JEE mock tests, JEE problem solving",
  },
  {
    id: 4,
    title: "NEET Aspirants Learn with NCERT-Focused Support",
    seoTitle:
      "NEET UG Preparation with NCERT Biology Chemistry Physics and Tests",
    subtitle:
      "NEET aspirants learn Physics, Chemistry and Biology with NCERT-focused notes, Biology diagrams, MCQ practice, doubt solving and progress tracking.",
    image: img4,
    alt: "NEET UG students learning Biology Chemistry Physics with NCERT notes MCQ practice and doubt solving",
    tag: "NEET UG",
    link: "/courses",
    audience:
      "Best for NEET UG aspirants who need NCERT Biology revision, Physics numericals, Chemistry concepts, mock tests and mentor guidance.",
    keywords:
      "NEET UG online coaching, NEET Biology notes, NCERT Biology revision, NEET Physics Chemistry Biology, medical entrance coaching, NEET mock tests, NEET doubt solving",
  },
];

const sectionKeywords = [
  "Mediit Academy learning outcomes",
  "student success stories",
  "online learning results",
  "Class 7 online classes",
  "Class 8 online tuition",
  "Class 9 Maths Science coaching",
  "Class 10 board preparation",
  "CBSE NCERT learning",
  "JEE Main online coaching",
  "NEET UG online coaching",
  "school foundation program",
  "live online classes",
  "study notes",
  "mock tests",
  "DPP practice",
  "mentor support",
  "student performance tracking",
  "doubt solving",
].join(", ");

const SuccessStoriesShowcase: React.FC = () => {
  const [activeCard, setActiveCard] = useState<number>(4);

  const successStoriesSchema = useMemo(
    () => ({
      "@context": "https://schema.org",
      "@type": "ItemList",
      name: "Mediit Academy Learning Outcomes and Student Preparation Paths",
      description:
        "Learning outcome section showing how students can prepare for school foundation, CBSE board exams, JEE Main and NEET UG with live classes, notes, tests and mentor support.",
      keywords: sectionKeywords,
      itemListElement: stories.map((story, index) => ({
        "@type": "ListItem",
        position: index + 1,
        item: {
          "@type": "Course",
          name: story.seoTitle,
          description: story.subtitle,
          educationalLevel: story.tag,
          audience: {
            "@type": "EducationalAudience",
            audienceType: story.audience,
          },
          keywords: story.keywords,
          provider: {
            "@type": "EducationalOrganization",
            name: "Mediit Academy",
            url: "/",
          },
          url: story.link,
        },
      })),
    }),
    []
  );

  return (
    <section
      id="success-stories"
      className="relative overflow-hidden bg-white py-14 font-['Inter'] sm:py-16 lg:py-20"
      aria-labelledby="success-stories-heading"
    >
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify(successStoriesSchema),
        }}
      />

      <div className="mx-auto max-w-[1380px] px-4 sm:px-6 lg:px-8 xl:px-4">
        <div className="mx-auto mb-10 max-w-3xl text-center">
          <p className="text-sm font-black uppercase tracking-[0.18em] text-blue-600">
            Learning Outcomes
          </p>

          <h2
            id="success-stories-heading"
            className="mt-3 text-3xl font-black leading-tight tracking-[-0.04em] text-slate-950 sm:text-4xl lg:text-[48px]"
          >
            Better Learning Outcomes for School, Board, JEE and NEET Students
          </h2>

          <p className="mt-4 text-base leading-7 text-slate-600">
            Mediit Academy helps students learn with structured live classes,
            subject-wise notes, daily practice, mock tests, mentor guidance,
            doubt solving and performance tracking for consistent exam
            preparation.
          </p>

          <p className="sr-only">{sectionKeywords}</p>
        </div>

        {/* Desktop Hover Cards */}
        <div className="hidden h-[360px] gap-5 lg:flex">
          {stories.map((story) => {
            const isActive = activeCard === story.id;

            return (
              <a
                key={story.id}
                href={story.link}
                title={story.seoTitle}
                aria-label={`${story.seoTitle}. ${story.audience}`}
                onMouseEnter={() => setActiveCard(story.id)}
                onFocus={() => setActiveCard(story.id)}
                className={`group relative overflow-hidden rounded-[28px] bg-slate-900 shadow-lg transition-all duration-500 ease-out ${
                  isActive ? "flex-[2.1]" : "flex-[0.72]"
                }`}
              >
                <article>
                  <img
                    src={story.image}
                    alt={story.alt}
                    loading="lazy"
                    decoding="async"
                    className={`absolute inset-0 h-full w-full object-cover transition-all duration-500 ${
                      isActive ? "scale-105 opacity-95" : "scale-100 opacity-70"
                    }`}
                  />

                  <div className="absolute inset-0 bg-gradient-to-t from-slate-950 via-slate-950/35 to-transparent" />

                  {!isActive && (
                    <div className="absolute inset-0 flex items-end justify-center p-6">
                      <div className="flex rotate-[-90deg] items-center gap-3 whitespace-nowrap">
                        <span className="rounded-full bg-white/95 px-3 py-1 text-xs font-black text-slate-950 shadow-sm">
                          {story.tag}
                        </span>

                        <p className="text-xl font-black text-white">
                          {story.tag}
                        </p>
                      </div>
                    </div>
                  )}

                  {isActive && (
                    <div className="absolute inset-0 flex flex-col justify-end p-7">
                      <span className="mb-4 w-fit rounded-full bg-white px-3 py-1 text-xs font-black text-slate-950 shadow-sm">
                        {story.tag}
                      </span>

                      <h3 className="max-w-xl text-3xl font-black leading-tight tracking-[-0.035em] text-white">
                        {story.title}
                      </h3>

                      <p className="mt-2 text-sm font-bold text-cyan-100">
                        {story.seoTitle}
                      </p>

                      <p className="mt-3 max-w-lg text-sm leading-6 text-white/80">
                        {story.subtitle}
                      </p>

                      <p className="sr-only">
                        {story.audience}. Keywords: {story.keywords}
                      </p>

                      <div className="mt-5 flex items-center gap-3">
                        <span className="flex h-12 w-12 items-center justify-center rounded-full bg-white text-lg text-slate-950 shadow-lg transition-all group-hover:scale-105">
                          ▶
                        </span>

                        <p className="text-sm font-black text-white">
                          Explore course
                        </p>
                      </div>
                    </div>
                  )}
                </article>
              </a>
            );
          })}
        </div>

        {/* Mobile / Tablet Cards */}
        <div className="grid grid-cols-1 gap-5 lg:hidden">
          {stories.map((story) => (
            <a
              key={story.id}
              href={story.link}
              title={story.seoTitle}
              aria-label={`${story.seoTitle}. ${story.audience}`}
              className="group overflow-hidden rounded-[28px] border border-slate-100 bg-white shadow-sm transition-all hover:-translate-y-1 hover:shadow-xl"
            >
              <article>
                <div className="relative h-[230px] overflow-hidden">
                  <img
                    src={story.image}
                    alt={story.alt}
                    loading="lazy"
                    decoding="async"
                    className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
                  />

                  <div className="absolute inset-0 bg-gradient-to-t from-slate-950/60 via-transparent to-transparent" />

                  <span className="absolute left-5 top-5 rounded-full bg-white px-3 py-1 text-xs font-black text-slate-950 shadow-sm">
                    {story.tag}
                  </span>
                </div>

                <div className="p-5">
                  <h3 className="text-2xl font-black leading-tight tracking-[-0.035em] text-slate-950">
                    {story.title}
                  </h3>

                  <p className="mt-2 text-sm font-bold text-blue-700">
                    {story.seoTitle}
                  </p>

                  <p className="mt-3 text-sm leading-6 text-slate-600">
                    {story.subtitle}
                  </p>

                  <p className="sr-only">
                    {story.audience}. Keywords: {story.keywords}
                  </p>

                  <p className="mt-4 text-sm font-black text-blue-600">
                    Explore course →
                  </p>
                </div>
              </article>
            </a>
          ))}
        </div>

        <div className="mt-10 flex flex-col items-center justify-center gap-3 sm:flex-row">
          <a
            href="/courses"
            aria-label="View all Mediit Academy courses for school foundation board exams JEE Main and NEET UG"
            className="rounded-xl bg-[#101827] px-7 py-3 text-sm font-black text-white shadow-lg transition-all hover:-translate-y-0.5 hover:bg-slate-800"
          >
            View All Courses
          </a>

          <a
            href="/login"
            aria-label="Start learning with Mediit Academy live classes notes tests and mentor support"
            className="rounded-xl border border-slate-300 bg-white px-7 py-3 text-sm font-black text-slate-950 transition-all hover:-translate-y-0.5 hover:bg-slate-50"
          >
            Start Learning
          </a>
        </div>
      </div>
    </section>
  );
};

export default SuccessStoriesShowcase;